import { useState, useEffect, useCallback, useContext } from 'react';
import izenuAPI from "../../services/izenuAPI";
import UpdateAudioTitleModal from '../../component/artist/UpdateAudioTitleModal';
import DeleteAudioConfirmationModal from '../../component/artist/DeleteAudioConfirmationModal';
import { UserContext } from '../../Context/UserContext';
import Player from '../../component/player/Player';
import GENRES from '../../utils/Genres';

const CDN_URL = 'https://dpagz47n4g1lw.cloudfront.net';

const StateBadge = ({ state }) => {
    const config = {
        approved: { label: 'Aprovada', bg: '#dcfce7', color: '#166534' },
        rejected: { label: 'Rejeitada', bg: '#fef2f2', color: '#991b1b' },
        pending: { label: 'Pendente', bg: '#fefce8', color: '#854d0e' },
        unreviewed: { label: 'Pendente', bg: '#fefce8', color: '#854d0e' },
    };
    const c = config[state] || config.unreviewed;
    return (
        <span style={{ fontSize: 11, fontWeight: 600, background: c.bg, color: c.color, padding: '2px 10px', borderRadius: 12 }}>
            {c.label}
        </span>
    );
};

const formatDuration = (ms) => {
    if (!ms) return '--:--';
    const min = Math.floor(ms / 60000);
    const sec = Math.floor((ms % 60000) / 1000);
    return `${min}:${sec.toString().padStart(2, '0')}`;
};

// ---- Edit Profile Modal ----
function EditProfileModal({ isOpen, onClose, profile, onSaved }) {
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);

    useEffect(() => {
        if (profile) {
            setForm({
                artist_name: profile.artist_name || '',
                name: profile.name || '',
                email: profile.email || '',
                phone: profile.phone || '',
                bio: profile.bio || '',
                genre: profile.genre || '',
            });
            setPhotoFile(null);
            setPhotoPreview(null);
        }
    }, [profile]);

    const handlePhotoChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setPhotoFile(file);
            setPhotoPreview(URL.createObjectURL(file));
        }
    };

    const handleSave = async () => {
        setSaving(true);
        const userId = profile.user_id;
        try {
            // Upload photo if changed
            if (photoFile) {
                const fd = new FormData();
                fd.append('image', photoFile);
                fd.append('user_id', userId);
                await izenuAPI.post('/user/updateProfilePhoto.php', fd);
            }
            // Update basic info + genre (returns user_data)
            const { data: basicRes } = await izenuAPI.post('/user/updateBasicInfo.php', {
                user_id: userId,
                name: form.name,
                bio: form.bio,
                address: '',
                artist_name: form.artist_name,
                genre: form.genre,
            });
            // Update contact info (phone only — email is read-only)
            await izenuAPI.post('/user/updateContactInfo.php', {
                user_id: userId,
                phone: form.phone,
            });
            // Build updated profile from API response + form values
            const updated = basicRes.user_data ? { ...basicRes.user_data } : { ...profile };
            updated.phone = form.phone;
            updated.artist_name = form.artist_name;
            updated.genre = form.genre;
            onSaved(updated);
            onClose();
        } catch (err) {
            console.log(err);
            alert('Erro ao guardar perfil');
        }
        setSaving(false);
    };

    if (!isOpen) return null;

    const photoUrl = photoPreview || (profile?.photo ? `${CDN_URL}/users/${profile.user_id}/profile_photos/${profile.photo}` : null);

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={onClose}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 500, width: '90%', maxHeight: '90vh', overflowY: 'auto' }}
                 onClick={e => e.stopPropagation()}>
                <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, color: '#1f2937' }}>Editar Perfil</h3>

                {/* Photo */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                    <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#e5e7eb', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {photoUrl ? <img src={photoUrl} alt="" style={{ width: 72, height: 72, objectFit: 'cover' }} /> : (
                            <svg style={{ width: 40, height: 40, color: '#9ca3af' }} fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 0a10 10 0 1 0 10 10A10.011 10.011 0 0 0 10 0Zm0 5a3 3 0 1 1 0 6 3 3 0 0 1 0-6Zm0 13a8.949 8.949 0 0 1-4.951-1.488A3.987 3.987 0 0 1 9 13h2a3.987 3.987 0 0 1 3.951 3.512A8.949 8.949 0 0 1 10 18Z" />
                            </svg>
                        )}
                    </div>
                    <label style={{ fontSize: 13, color: '#6366f1', cursor: 'pointer', fontWeight: 600 }}>
                        Alterar foto
                        <input type="file" accept="image/*" hidden onChange={handlePhotoChange} />
                    </label>
                </div>

                {/* Fields */}
                {[
                    { key: 'artist_name', label: 'Nome artístico' },
                    { key: 'name', label: 'Nome' },
                    { key: 'phone', label: 'Telefone' },
                ].map(f => (
                    <div key={f.key} style={{ marginBottom: 14 }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>{f.label}</label>
                        <input type="text" value={form[f.key] || ''} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
                    </div>
                ))}
                {/* Email — read-only */}
                <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Email</label>
                    <input type="email" value={form.email || ''} disabled
                        style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, boxSizing: 'border-box', background: '#f3f4f6', color: '#9ca3af', cursor: 'not-allowed' }} />
                </div>
                {/* Genre dropdown */}
                <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Género musical</label>
                    <select value={form.genre || ''} onChange={e => setForm(p => ({ ...p, genre: e.target.value }))}
                        style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box', background: '#fff' }}>
                        <option value="">Selecionar género</option>
                        {GENRES.map(g => <option key={g.name} value={g.name}>{g.name}</option>)}
                    </select>
                </div>
                <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Bio</label>
                    <textarea value={form.bio || ''} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))} rows={3}
                        style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }} />
                </div>

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                    <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 14 }}>Cancelar</button>
                    <button onClick={handleSave} disabled={saving}
                        style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, opacity: saving ? 0.6 : 1 }}>
                        {saving ? 'A guardar...' : 'Guardar'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---- Song Row ----
function SongRow({ song, index, onToggleVisibility, onRefresh }) {
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [toggling, setToggling] = useState(false);
    const { setCurrentTrack } = useContext(UserContext);

    const track = {
        id: song.music_id,
        title: song.title,
        artist: song.singer,
        coverUrl: song.cover,
        audioSrc: song.music_url,
    };

    const handleToggle = async () => {
        setToggling(true);
        try {
            const newActive = song.active === 1 ? 0 : 1;
            await izenuAPI.post('/music/toggleVisibility.php', { music_id: song.music_id, active: newActive });
            onToggleVisibility(song.music_id, newActive);
        } catch (err) {
            console.log(err);
            alert('Erro ao alterar visibilidade');
        }
        setToggling(false);
    };

    const handleDelete = async () => {
        try {
            const { data: r } = await izenuAPI.post('/browser/audio/delete.php', { music_id: song.music_id });
            if (r.response) {
                onRefresh();
            } else {
                alert('Não foi possível apagar');
            }
        } catch (err) {
            console.log(err);
        }
    };

    const handleSaveTitle = async (newTitle) => {
        try {
            const { data: r } = await izenuAPI.post('/music/updateTitle.php', { music_id: song.music_id, title: newTitle });
            if (r.response) {
                onRefresh();
            } else {
                alert('Não foi possível alterar o título');
            }
        } catch (err) {
            console.log(err);
        }
    };

    return (
        <>
            <div className="group hover:bg-gray-800 transition duration-200 cursor-pointer"
                 onClick={() => setCurrentTrack(track)}>
                <div className="grid gap-4 px-6 py-3 items-center" style={{ gridTemplateColumns: '40px 2fr 1fr 90px 60px 120px' }}>
                    <div className="text-gray-400 text-center">{index}</div>
                    <div className="flex items-center gap-3 min-w-0">
                        {song.cover ? (
                            <img src={song.cover} alt="" className="w-10 h-10 rounded flex-shrink-0 object-cover" />
                        ) : (
                            <div className="w-10 h-10 rounded flex-shrink-0 bg-gray-700" />
                        )}
                        <div className="min-w-0">
                            <p className="text-gray-50 font-medium truncate m-0">{song.title}</p>
                            <p className="text-gray-400 text-xs m-0">{formatDuration(song.duration)}</p>
                        </div>
                    </div>
                    <div className="text-gray-400 text-sm truncate">{song.genre}</div>
                    <div><StateBadge state={song.music_state} /></div>
                    <div className="flex justify-center" onClick={e => e.stopPropagation()}>
                        <button onClick={handleToggle} disabled={toggling}
                            title={song.active === 1 ? 'Visível (clique para ocultar)' : 'Oculta (clique para tornar visível)'}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                            {song.active === 1 ? (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="#818cf8" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="#6b7280" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                                </svg>
                            )}
                        </button>
                    </div>
                    <div className="flex gap-2 justify-end" onClick={e => e.stopPropagation()}>
                        <button onClick={() => setIsEditOpen(true)} className="text-gray-400 hover:text-indigo-400 text-xs" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>Editar</button>
                        <button onClick={() => setIsDeleteOpen(true)} className="text-gray-400 hover:text-red-400 text-xs" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>Eliminar</button>
                    </div>
                </div>
            </div>
            <UpdateAudioTitleModal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} currentTitle={song.title} onSave={handleSaveTitle} isLoading={false} />
            <DeleteAudioConfirmationModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={handleDelete} isLoading={false} audioTitle={song.title} />
        </>
    );
}

// Revenue arrives split by currency — join only the ones that actually have value.
function formatRevenue(aoa, usd) {
    const parts = [];
    if (aoa > 0) parts.push(`${Math.round(aoa).toLocaleString('pt-PT')} Kz`);
    if (usd > 0) parts.push(`$${usd.toFixed(2)}`);
    return parts.join(' + ');
}

// ---- Release Card ----
function ReleaseCard({ release, sales, isSelected, onClick }) {
    const typeLabels = { single: 'Single', ep: 'EP', album: 'Álbum' };
    const typeColors = { single: '#6366f1', ep: '#0891b2', album: '#7c3aed' };
    const isDraft = release.status === 'draft';
    const forSale = release.for_sale === 1;
    const soldCount = sales?.sales_count || 0;
    const pendingSales = sales?.pending_sales || 0;
    const revenue = formatRevenue(sales?.revenue_aoa || 0, sales?.revenue_usd || 0);
    return (
        <div onClick={onClick} style={{ background: isSelected ? '#312e81' : '#1f2937', borderRadius: 12, overflow: 'hidden', width: 160, cursor: 'pointer', border: isSelected ? '2px solid #6366f1' : '2px solid transparent', transition: 'all 0.2s', position: 'relative' }}>
            {forSale && (
                <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 1, fontSize: 10, fontWeight: 700, background: '#f59e0b', color: '#78350f', padding: '2px 8px', borderRadius: 10 }}>
                    À VENDA
                </div>
            )}
            {isDraft && (
                <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 1, fontSize: 10, fontWeight: 700, background: '#fbbf24', color: '#78350f', padding: '2px 8px', borderRadius: 10 }}>
                    Rascunho
                </div>
            )}
            <div style={{ width: 156, height: 156, background: '#374151' }}>
                {release.artwork ? (
                    <img src={release.artwork} alt="" style={{ width: 156, height: 156, objectFit: 'cover' }} />
                ) : (
                    <div style={{ width: 156, height: 156, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg style={{ width: 40, height: 40, color: '#6b7280' }} fill="currentColor" viewBox="0 0 20 20">
                            <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                        </svg>
                    </div>
                )}
            </div>
            <div style={{ padding: '10px 12px' }}>
                <p style={{ margin: 0, color: '#f9fafb', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{release.title}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, background: typeColors[release.type] || '#6366f1', color: '#fff', padding: '1px 8px', borderRadius: 10 }}>
                        {typeLabels[release.type] || release.type}
                    </span>
                    <span style={{ fontSize: 11, color: '#9ca3af' }}>{release.track_count} {release.track_count === 1 ? 'faixa' : 'faixas'}</span>
                </div>
                {forSale && (
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #374151' }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 6 }}>
                            <span style={{ fontSize: 11, color: soldCount > 0 ? '#f9fafb' : '#6b7280', fontWeight: soldCount > 0 ? 600 : 400 }}>
                                {soldCount} {soldCount === 1 ? 'venda' : 'vendas'}
                            </span>
                            {revenue && (
                                <span style={{ fontSize: 11, color: '#34d399', fontWeight: 600, whiteSpace: 'nowrap' }}>{revenue}</span>
                            )}
                        </div>
                        {pendingSales > 0 && (
                            <p style={{ margin: '3px 0 0', fontSize: 10, color: '#fbbf24' }}>
                                {pendingSales} por confirmar
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// ---- Release Track Row ----
function ReleaseTrackRow({ track, index, onPlay }) {
    return (
        <div className="hover:bg-gray-700 transition duration-150 cursor-pointer" onClick={() => onPlay(track)}
             style={{ display: 'grid', gridTemplateColumns: '30px 2fr 1fr 60px', gap: 12, padding: '8px 16px', alignItems: 'center' }}>
            <span style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center' }}>{index}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                {track.cover ? (
                    <img src={track.cover} alt="" style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                    <div style={{ width: 32, height: 32, borderRadius: 4, background: '#374151', flexShrink: 0 }} />
                )}
                <div style={{ minWidth: 0 }}>
                    <p style={{ margin: 0, color: '#f9fafb', fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{track.title}</p>
                    <p style={{ margin: 0, color: '#9ca3af', fontSize: 11 }}>{track.singer}</p>
                </div>
            </div>
            <span style={{ color: '#9ca3af', fontSize: 12 }}>{track.genre}</span>
            <span style={{ color: '#6b7280', fontSize: 12, textAlign: 'right' }}>{formatDuration(track.duration)}</span>
        </div>
    );
}

// ---- Edit Release Modal ----
function EditReleaseModal({ isOpen, onClose, releaseId, onSaved }) {
    const { setCurrentTrack } = useContext(UserContext);
    const [release, setRelease] = useState(null);
    const [tracks, setTracks] = useState([]);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [artworkFile, setArtworkFile] = useState(null);
    const [artworkPreview, setArtworkPreview] = useState(null);

    useEffect(() => {
        if (isOpen && releaseId) {
            setArtworkFile(null);
            setArtworkPreview(null);
            setRelease(null);
            setTracks([]);
            fetchRelease(releaseId);
        }
    }, [isOpen, releaseId]);

    const fetchRelease = async (id) => {
        setLoading(true);
        try {
            const { data: r } = await izenuAPI.get(`/release/fetch.php?id=${id}&include_all_tracks=1`);
            if (r?.response && r.data) {
                setRelease(r.data);
                setTracks(r.data.tracks || []);
                setForm({
                    title: r.data.title || '',
                    upc: r.data.upc || '',
                    genre: r.data.genre || '',
                    description: r.data.description || '',
                });
            }
        } catch (err) {
            console.log(err);
        }
        setLoading(false);
    };

    const handleArtworkChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setArtworkFile(file);
            setArtworkPreview(URL.createObjectURL(file));
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            if (artworkFile) {
                const fd = new FormData();
                fd.append('artwork', artworkFile);
                fd.append('release_id', releaseId);
                await izenuAPI.post('/release/uploadArtwork.php', fd);
            }
            await izenuAPI.post('/release/update.php', {
                release_id: releaseId,
                title: form.title,
                genre: form.genre,
                upc: form.upc,
                description: form.description,
            });
            onSaved();
            onClose();
        } catch (err) {
            console.log(err);
            alert('Erro ao guardar release');
        }
        setSaving(false);
    };

    const handlePlayTrack = (track) => {
        setCurrentTrack({
            id: track.music_id,
            title: track.title,
            artist: track.singer,
            coverUrl: track.cover,
            audioSrc: track.music_url,
        });
    };

    if (!isOpen) return null;

    const artworkUrl = artworkPreview || release?.artwork || null;

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={onClose}>
            <div style={{ background: '#1f2937', borderRadius: 12, padding: 28, maxWidth: 560, width: '90%', maxHeight: '90vh', overflowY: 'auto' }}
                 onClick={e => e.stopPropagation()}>
                {loading ? (
                    <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>A carregar...</div>
                ) : release ? (
                    <>
                        <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, color: '#f9fafb' }}>Editar Release</h3>

                        {/* Artwork */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                            <div style={{ width: 120, height: 120, borderRadius: 8, background: '#374151', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {artworkUrl ? <img src={artworkUrl} alt="" style={{ width: 120, height: 120, objectFit: 'cover' }} /> : (
                                    <svg style={{ width: 40, height: 40, color: '#6b7280' }} fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                                    </svg>
                                )}
                            </div>
                            <label style={{ fontSize: 13, color: '#818cf8', cursor: 'pointer', fontWeight: 600 }}>
                                Alterar capa
                                <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={handleArtworkChange} />
                            </label>
                        </div>

                        {/* Title */}
                        <div style={{ marginBottom: 14 }}>
                            <label style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', display: 'block', marginBottom: 4 }}>Título</label>
                            <input type="text" value={form.title || ''} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #374151', fontSize: 14, boxSizing: 'border-box', background: '#111827', color: '#f9fafb' }} />
                        </div>

                        {/* UPC */}
                        <div style={{ marginBottom: 14 }}>
                            <label style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', display: 'block', marginBottom: 4 }}>UPC</label>
                            <input type="text" value={form.upc || ''} onChange={e => setForm(p => ({ ...p, upc: e.target.value }))}
                                placeholder="Código UPC/EAN"
                                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #374151', fontSize: 14, boxSizing: 'border-box', background: '#111827', color: '#f9fafb' }} />
                        </div>

                        {/* Genre */}
                        <div style={{ marginBottom: 14 }}>
                            <label style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', display: 'block', marginBottom: 4 }}>Género</label>
                            <select value={form.genre || ''} onChange={e => setForm(p => ({ ...p, genre: e.target.value }))}
                                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #374151', fontSize: 14, boxSizing: 'border-box', background: '#111827', color: '#f9fafb' }}>
                                <option value="">Selecionar género</option>
                                {GENRES.map(g => <option key={g.name} value={g.name}>{g.name}</option>)}
                            </select>
                        </div>

                        {/* Description */}
                        <div style={{ marginBottom: 20 }}>
                            <label style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', display: 'block', marginBottom: 4 }}>Descrição</label>
                            <textarea value={form.description || ''} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={3}
                                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #374151', fontSize: 14, resize: 'vertical', boxSizing: 'border-box', background: '#111827', color: '#f9fafb' }} />
                        </div>

                        {/* Buttons */}
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginBottom: 24 }}>
                            <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #374151', background: 'transparent', cursor: 'pointer', fontSize: 14, color: '#d1d5db' }}>Cancelar</button>
                            <button onClick={handleSave} disabled={saving}
                                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, opacity: saving ? 0.6 : 1 }}>
                                {saving ? 'A guardar...' : 'Guardar'}
                            </button>
                        </div>

                        {/* Tracks */}
                        {tracks.length > 0 && (
                            <div style={{ borderTop: '1px solid #374151', paddingTop: 16 }}>
                                <h4 style={{ margin: '0 0 12px', color: '#d1d5db', fontSize: 14, fontWeight: 600 }}>
                                    Faixas ({tracks.length})
                                </h4>
                                {tracks.map((t, i) => (
                                    <ReleaseTrackRow key={t.music_id} track={t} index={i + 1} onPlay={handlePlayTrack} />
                                ))}
                            </div>
                        )}
                    </>
                ) : (
                    <div style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>Erro ao carregar release</div>
                )}
            </div>
        </div>
    );
}

// ---- Main Page ----
export default function ArtistHome() {
    const { currentTrack, setCurrentTrack } = useContext(UserContext);
    const [profile, setProfile] = useState(null);
    const [songs, setSongs] = useState([]);
    const [releases, setReleases] = useState([]);
    const [salesByRelease, setSalesByRelease] = useState({});
    const [salesTotals, setSalesTotals] = useState(null);
    const [albumSales, setAlbumSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editProfileOpen, setEditProfileOpen] = useState(false);
    const [editReleaseId, setEditReleaseId] = useState(null);

    const userId = localStorage.getItem('id');

    const fetchProfile = useCallback(async () => {
        try {
            // Get fresh user data from localStorage (populated during impersonation/login)
            const stored = localStorage.getItem('user');
            if (stored) {
                const parsed = JSON.parse(stored);
                setProfile(parsed);
            }
        } catch (err) {
            console.log(err);
        }
    }, []);

    const fetchSongs = useCallback(async () => {
        try {
            const { data: r } = await izenuAPI.get('/music/fetchArtistSongs.php');
            if (r?.response && r.data) {
                setSongs(r.data);
            }
        } catch (err) {
            console.log(err);
        }
    }, []);

    const fetchReleases = useCallback(async () => {
        if (!userId) return;
        try {
            const { data: r } = await izenuAPI.get(`/release/fetchArtistReleases.php?artistId=${userId}&limit=50&include_drafts=1`);
            if (r?.response && r.data) {
                setReleases(r.data);
            }
        } catch (err) {
            console.log(err);
        }
    }, [userId]);

    const fetchSales = useCallback(async () => {
        try {
            const { data: r } = await izenuAPI.get('/artist/releaseSales.php');
            if (r?.response) {
                const map = {};
                (r.data || []).forEach(s => { map[s.release_id] = s; });
                setSalesByRelease(map);
                setAlbumSales(r.albums || []);
                setSalesTotals(r.totals || null);
            }
        } catch (err) {
            console.log(err);
        }
    }, []);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            await fetchProfile();
            await Promise.all([fetchSongs(), fetchReleases(), fetchSales()]);
            setLoading(false);
        };
        load();
    }, [fetchProfile, fetchSongs, fetchReleases, fetchSales]);

    const handleToggleVisibility = (musicId, newActive) => {
        setSongs(prev => prev.map(s => s.music_id === musicId ? { ...s, active: newActive } : s));
    };

    const handleProfileSaved = (updatedUser) => {
        if (updatedUser) {
            setProfile(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
            localStorage.setItem('name', updatedUser.name);
            if (updatedUser.artist_name) {
                localStorage.setItem('impersonating_name', updatedUser.artist_name);
            }
        }
    };

    const photoUrl = profile?.photo ? `${CDN_URL}/users/${profile.user_id}/profile_photos/${profile.photo}` : null;

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20 bg-gray-900 min-h-screen">
                <svg aria-hidden="true" className="inline w-8 h-8 text-gray-200 animate-spin fill-amber-400" viewBox="0 0 100 101" fill="none">
                    <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                    <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                </svg>
                <p className="text-amber-500 font-light text-lg mt-3">A carregar...</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-screen bg-gray-900">
            {/* Profile Section */}
            {profile && (
                <div style={{ background: 'linear-gradient(135deg, #312e81, #581c87)', padding: '32px 40px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                        <div style={{ width: 88, height: 88, borderRadius: '50%', background: '#4b5563', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {photoUrl ? (
                                <img src={photoUrl} alt="" style={{ width: 88, height: 88, objectFit: 'cover' }} />
                            ) : (
                                <svg style={{ width: 50, height: 50, color: '#9ca3af' }} fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10 0a10 10 0 1 0 10 10A10.011 10.011 0 0 0 10 0Zm0 5a3 3 0 1 1 0 6 3 3 0 0 1 0-6Zm0 13a8.949 8.949 0 0 1-4.951-1.488A3.987 3.987 0 0 1 9 13h2a3.987 3.987 0 0 1 3.951 3.512A8.949 8.949 0 0 1 10 18Z" />
                                </svg>
                            )}
                        </div>
                        <div style={{ flex: 1 }}>
                            <h1 style={{ margin: 0, color: '#f9fafb', fontSize: 26, fontWeight: 700 }}>
                                {profile.artist_name || profile.name}
                            </h1>
                            {profile.artist_name && profile.name !== profile.artist_name && (
                                <p style={{ margin: '2px 0 0', color: '#c4b5fd', fontSize: 14 }}>{profile.name}</p>
                            )}
                            <p style={{ margin: '4px 0 0', color: '#d1d5db', fontSize: 13 }}>
                                {profile.email}
                                {profile.phone ? ` · ${profile.phone}` : ''}
                            </p>
                            {profile.bio && (
                                <p style={{ margin: '6px 0 0', color: '#a5b4fc', fontSize: 13, maxWidth: 500, lineHeight: 1.4,
                                    overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                    {profile.bio}
                                </p>
                            )}
                            {profile.genre && (
                                <span style={{ display: 'inline-block', marginTop: 8, fontSize: 11, fontWeight: 600, background: 'rgba(255,255,255,0.15)', color: '#e0e7ff', padding: '2px 10px', borderRadius: 10 }}>
                                    {profile.genre}
                                </span>
                            )}
                        </div>
                        <button onClick={() => setEditProfileOpen(true)}
                            style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.1)', color: '#f9fafb', cursor: 'pointer', fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
                            Editar perfil
                        </button>
                    </div>
                </div>
            )}

            {/* Songs Section */}
            <div style={{ padding: '0' }}>
                <div style={{ background: 'linear-gradient(180deg, #581c87, #111827)', padding: '24px 40px 12px' }}>
                    <h2 style={{ margin: 0, color: '#f9fafb', fontSize: 20, fontWeight: 700 }}>As tuas músicas</h2>
                    <p style={{ margin: '4px 0 0', color: '#9ca3af', fontSize: 14 }}>{songs.length} {songs.length === 1 ? 'música' : 'músicas'}</p>
                </div>

                <div className="bg-gray-900">
                    {/* Table header */}
                    <div className="px-6 py-2 text-gray-500 text-xs font-medium border-b border-gray-800"
                         style={{ display: 'grid', gridTemplateColumns: '40px 2fr 1fr 90px 60px 120px', gap: 16 }}>
                        <div className="text-center">#</div>
                        <div>TÍTULO</div>
                        <div>GÉNERO</div>
                        <div>ESTADO</div>
                        <div className="text-center">VIS.</div>
                        <div className="text-right">ACÇÕES</div>
                    </div>

                    {songs.length === 0 ? (
                        <div className="flex items-center justify-center py-16">
                            <p className="text-gray-500">Nenhuma música encontrada</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-800">
                            {songs.map((s, i) => (
                                <SongRow key={s.music_id} song={s} index={i + 1}
                                    onToggleVisibility={handleToggleVisibility} onRefresh={fetchSongs} />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Discography Section */}
            <div style={{ padding: '32px 40px', paddingBottom: 150, background: '#111827' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                    <h2 style={{ margin: 0, color: '#f9fafb', fontSize: 20, fontWeight: 700 }}>Discografia</h2>
                    {salesTotals && salesTotals.sales_count > 0 && (
                        <span style={{ fontSize: 13, color: '#9ca3af' }}>
                            {salesTotals.sales_count} {salesTotals.sales_count === 1 ? 'venda' : 'vendas'}
                            {' · '}
                            <span style={{ color: '#34d399', fontWeight: 600 }}>
                                {formatRevenue(salesTotals.revenue_aoa, salesTotals.revenue_usd)}
                            </span>
                        </span>
                    )}
                </div>
                {releases.length === 0 ? (
                    <p style={{ color: '#6b7280', fontSize: 14 }}>Sem releases</p>
                ) : (
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        {releases.map(r => (
                            <ReleaseCard key={r.id} release={r} sales={salesByRelease[r.id]}
                                isSelected={editReleaseId === r.id}
                                onClick={() => setEditReleaseId(r.id)} />
                        ))}
                    </div>
                )}

                {/* Albums live in a separate legacy table with no link back to a release,
                    so they get their own block instead of being folded into a card. */}
                {albumSales.length > 0 && (
                    <div style={{ marginTop: 20, padding: '14px 16px', background: '#1f2937', borderRadius: 10, border: '1px solid #374151' }}>
                        <p style={{ margin: '0 0 10px', fontSize: 12, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                            Vendas na loja de álbuns da app
                        </p>
                        {albumSales.map(a => (
                            <div key={a.album_id} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, padding: '4px 0' }}>
                                <span style={{ color: '#f9fafb', fontSize: 13, fontWeight: 500 }}>{a.title}</span>
                                <span style={{ fontSize: 12, color: '#9ca3af', textAlign: 'right' }}>
                                    {a.sales_count} {a.sales_count === 1 ? 'venda' : 'vendas'}
                                    {formatRevenue(a.revenue_aoa, a.revenue_usd) && (
                                        <> · <span style={{ color: '#34d399', fontWeight: 600 }}>{formatRevenue(a.revenue_aoa, a.revenue_usd)}</span></>
                                    )}
                                    {a.pending_sales > 0 && (
                                        <> · <span style={{ color: '#fbbf24' }}>{a.pending_sales} por confirmar</span></>
                                    )}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Edit Profile Modal */}
            <EditProfileModal isOpen={editProfileOpen} onClose={() => setEditProfileOpen(false)}
                profile={profile} onSaved={handleProfileSaved} />

            {/* Edit Release Modal */}
            <EditReleaseModal isOpen={!!editReleaseId} onClose={() => setEditReleaseId(null)}
                releaseId={editReleaseId} onSaved={fetchReleases} />

            {/* Player */}
            <Player />
        </div>
    );
}
